import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  BarChart,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Form,
  FormActions,
  FormSection,
  GridFrame,
  GridScroll,
  LoadingState,
  NoResultsState,
  PageHeader,
  SelectField,
  StatusChip,
  Table,
  Td,
  TextField,
  Th,
  Tr,
  statusTone,
} from '@tessma/design-system';
import { ApiError } from '../work/gateway.js';
import type {
  AnalyticsGateway,
  DatasetSummary,
  ExportEntry,
  MetricCard,
  ReportResult,
  ReportSummary,
  SavedSearch,
  SearchResults,
} from './gateway.js';

/**
 * The seven Stage 6 surfaces, composed ENTIRELY from the frozen design system.
 *
 * Not one new visual component, not one colour, not one measurement. Where a
 * screen needed something the system does not have, the answer was to use what
 * it does have.
 *
 * **THE UI DECIDES NOTHING.** It does not filter rows, re-check permissions,
 * recompute a total, or hold a copy of any entitlement rule. Every number on
 * these screens came from the API, which computed it over the authorised
 * relation in SQL. A screen that re-added its own rows would be a second answer
 * to a question the database has already answered correctly.
 *
 * **A REFUSED METRIC IS NOT ZERO.** Zero is a data claim — "there are none of
 * these" — and a caller who may not see a figure has been told nothing about
 * whether it is large or small. So the card says so in words and shows no
 * number at all.
 */

function useAsync<T>(load: () => Promise<T>, deps: readonly unknown[]) {
  const [state, setState] = useState<
    | { status: 'idle' }
    | { status: 'loading' }
    | { status: 'ready'; value: T }
    | { status: 'failed'; error: ApiError }
  >({ status: 'loading' });

  const run = useCallback(() => {
    setState({ status: 'loading' });
    load().then(
      (value) => {
        setState({ status: 'ready', value });
      },
      (error: unknown) => {
        setState({
          status: 'failed',
          error:
            error instanceof ApiError
              ? error
              : new ApiError(0, 'Could not reach the server', 'Check your connection and retry.'),
        });
      },
    );
    // `load` is a fresh closure every render, so it cannot be the dependency —
    // depending on it would reload on every render forever. The list the CALLER
    // passes is the real one: what the request actually varies with.
  }, deps);

  useEffect(run, [run]);
  return { state, reload: run };
}

/**
 * The API's own words, shown as the API worded them.
 *
 * A 404 and a 403 mean different things and the SERVER decided which this was.
 * The browser repeating that decision — inferring "you lack permission" from a
 * 404, say — would undo the concealment the ordering exists to provide.
 */
function Refusal({ error, onRetry }: { readonly error: ApiError; readonly onRetry?: () => void }) {
  return (
    <ErrorState
      title={error.message}
      description={error.detail ?? 'The server did not say more.'}
      {...(onRetry === undefined ? {} : { onRetry })}
    />
  );
}

function when(iso: string | null): string {
  if (iso === null) return '—';
  return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
}

// ---- 1. Global search -------------------------------------------------------

export function GlobalSearchScreen({
  gateway,
  initialTerm = '',
}: {
  readonly gateway: AnalyticsGateway;
  /** A term the caller arrived with — running a saved search is the only source. */
  readonly initialTerm?: string;
}) {
  const [term, setTerm] = useState(initialTerm);
  const [submitted, setSubmitted] = useState(initialTerm);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | undefined>(undefined);
  const [saved, setSaved] = useState(false);

  const { state, reload } = useAsync<SearchResults | null>(
    () => (submitted === '' ? Promise.resolve(null) : gateway.search(submitted)),
    [gateway, submitted],
  );

  return (
    <div>
      <Card>
        <CardHeader title="Search" subtitle="Records you are permitted to see." />
        <Form
          onSubmit={(event) => {
            event.preventDefault();
            setSaved(false);
            setSubmitted(term.trim());
          }}
        >
          <TextField
            label="Search"
            name="q"
            value={term}
            onChange={(event) => {
              setTerm(event.target.value);
            }}
            helper="Words from a title or description."
          />
          <FormActions submitLabel="Search" />
        </Form>
      </Card>

      {submitted !== '' && state.status === 'loading' && <LoadingState title="Searching…" />}
      {state.status === 'failed' && <Refusal error={state.error} onRetry={reload} />}

      {state.status === 'ready' && state.value !== null && state.value.hits.length === 0 && (
        <Card>
          <NoResultsState
            title="Nothing matched"
            description={`No records you can see match “${submitted}”.`}
            onClear={() => {
              setTerm('');
              setSubmitted('');
            }}
          />
        </Card>
      )}

      {state.status === 'ready' && state.value !== null && state.value.hits.length > 0 && (
        <Card padding="flush">
          <CardHeader
            title={`${String(state.value.total)} result${state.value.total === 1 ? '' : 's'}`}
            subtitle="Ranked by relevance. Only records you may read appear."
            action={
              <Button
                variant="secondary"
                disabled={saving}
                onClick={() => {
                  setSaving(true);
                  setSaveError(undefined);
                  gateway
                    .saveSearch({ name: submitted, visibility: 'private', term: submitted })
                    .then(
                      () => {
                        setSaved(true);
                      },
                      (error: unknown) => {
                        setSaveError(
                          error instanceof ApiError ? error.message : 'Could not save the search',
                        );
                      },
                    )
                    .finally(() => {
                      setSaving(false);
                    });
                }}
              >
                Save this search
              </Button>
            }
          />
          {saved && <Alert tone="success" title="Saved" />}
          {saveError !== undefined && <Alert tone="danger" title={saveError} />}
          <GridFrame>
            <GridScroll>
              <Table label="Search results">
                <thead>
                  <Tr>
                    <Th>Title</Th>
                    <Th>Type</Th>
                    <Th>Detail</Th>
                    <Th>Match</Th>
                  </Tr>
                </thead>
                <tbody>
                  {state.value.hits.map((hit) => (
                    <Tr key={`${hit.resource}:${hit.recordId}`}>
                      <Td>{hit.title}</Td>
                      <Td variant="secondary">{hit.resource}</Td>
                      <Td variant="secondary">{hit.subtitle}</Td>
                      <Td>
                        <StatusChip tone={hit.exact ? 'success' : 'neutral'}>
                          {hit.exact ? 'exact' : 'similar'}
                        </StatusChip>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </GridScroll>
          </GridFrame>
        </Card>
      )}
    </div>
  );
}

// ---- 2. Saved searches ------------------------------------------------------

export function SavedSearchesScreen({
  gateway,
  onRun,
}: {
  readonly gateway: AnalyticsGateway;
  readonly onRun: (term: string) => void;
}) {
  const { state, reload } = useAsync<readonly SavedSearch[]>(
    () => gateway.savedSearches(),
    [gateway],
  );

  if (state.status === 'loading') return <LoadingState title="Loading your saved searches…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  if (state.value.length === 0) {
    return (
      <Card>
        <EmptyState
          icon="magnifying-glass"
          title="No saved searches yet"
          description="Save a search from the results screen and it appears here."
        />
      </Card>
    );
  }

  return (
    <Card padding="flush">
      <CardHeader
        title="Saved searches"
        subtitle="A saved search is a question. What it returns is decided every time by what you may see."
      />
      <GridFrame>
        <GridScroll>
          <Table label="Saved searches">
            <thead>
              <Tr>
                <Th>Name</Th>
                <Th>Visibility</Th>
                <Th>Last run</Th>
                <Th>Run</Th>
              </Tr>
            </thead>
            <tbody>
              {state.value.map((entry) => (
                <Tr key={entry.id}>
                  <Td>{entry.name}</Td>
                  <Td variant="secondary">
                    <StatusChip tone={entry.visibility === 'tenant' ? 'info' : 'neutral'}>
                      {entry.visibility === 'tenant' ? 'shared' : 'private'}
                    </StatusChip>
                  </Td>
                  <Td variant="secondary">{when(entry.lastRunAt)}</Td>
                  <Td>
                    <Button
                      variant="tertiary"
                      onClick={() => {
                        onRun(entry.criteria.term);
                      }}
                    >
                      Run
                    </Button>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </GridScroll>
      </GridFrame>
    </Card>
  );
}

// ---- 3. Reports list --------------------------------------------------------

export function ReportsListScreen({
  gateway,
  canManage,
  onOpen,
  onNew,
}: {
  readonly gateway: AnalyticsGateway;
  readonly canManage: boolean;
  readonly onOpen: (key: string) => void;
  readonly onNew: () => void;
}) {
  const { state, reload } = useAsync<readonly ReportSummary[]>(() => gateway.reports(), [gateway]);

  if (state.status === 'loading') return <LoadingState title="Loading reports…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  /**
   * THE SECOND MODULE WITH THE SAME DEFECT, and the reason `PageHeader` is a
   * shared pattern rather than a Finance component.
   *
   * This is the Reports module's landing page, so the shell's breadcrumb reads
   * "Reports › Overview" — and a card directly beneath it said "Reports" a
   * second time, in an `<h3>` under an `<h1>`, with the one action the page
   * exists for tucked inside that card's header. The table below it is still
   * named "Reports" for anyone reading by landmark.
   */
  const header = (
    <PageHeader
      description="Run a report and see the rows you are permitted to see."
      actions={
        // HIDDEN IS NOT REFUSED. The server checks again, and a caller who
        // reconstructs the request by hand gets the same 403 — or the same 404
        // if their tenant has not bought the builder. This is a courtesy.
        canManage ? (
          <Button variant="primary" onClick={onNew}>
            New report
          </Button>
        ) : undefined
      }
    />
  );

  if (state.value.length === 0) {
    return (
      <>
        {header}
        <Card>
          <EmptyState
            icon="chart-bar"
            title="No reports yet"
            description="Reports you can see appear here."
          />
        </Card>
      </>
    );
  }

  return (
    <>
      {header}
      <Card padding="flush">
        <GridFrame>
          <GridScroll>
            <Table label="Reports">
              <thead>
                <Tr>
                  <Th>Report</Th>
                  <Th>Dataset</Th>
                  <Th>Owner</Th>
                  <Th>Updated</Th>
                </Tr>
              </thead>
              <tbody>
                {state.value.map((report) => (
                  <Tr key={report.key}>
                    <Td>
                      <Button
                        variant="tertiary"
                        onClick={() => {
                          onOpen(report.key);
                        }}
                      >
                        {report.name}
                      </Button>
                    </Td>
                    <Td variant="secondary">{report.datasetKey}</Td>
                    <Td variant="secondary">
                      <StatusChip tone={report.ownership === 'platform' ? 'info' : 'neutral'}>
                        {report.ownership}
                      </StatusChip>
                    </Td>
                    <Td variant="secondary">{when(report.updatedAt)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </GridScroll>
        </GridFrame>
      </Card>
    </>
  );
}

// ---- 4. Report builder ------------------------------------------------------

interface BuilderProblem {
  /** Absent when the problem is the server's answer rather than one field's. */
  readonly field?: 'name' | 'dataset' | 'groupBy';
  readonly message: string;
}

/** The error presentation for one field, or nothing when the problem is elsewhere. */
function fieldProblem(
  problem: BuilderProblem | undefined,
  field: NonNullable<BuilderProblem['field']>,
): { readonly state?: 'error'; readonly message?: string } {
  if (problem?.field !== field) return {};
  return { state: 'error', message: problem.message };
}

export function ReportBuilderScreen({
  gateway,
  onCreated,
  onCancel,
}: {
  readonly gateway: AnalyticsGateway;
  readonly onCreated: (key: string) => void;
  readonly onCancel: () => void;
}) {
  const { state, reload } = useAsync<readonly DatasetSummary[]>(
    () => gateway.datasets(),
    [gateway],
  );
  const [name, setName] = useState('');
  const [datasetKey, setDatasetKey] = useState('');
  const [groupBy, setGroupBy] = useState('');
  // A problem BELONGS somewhere. One tied to a field is shown on that field and
  // nowhere else; a refusal from the server belongs to the form. Saying the same
  // sentence twice reads as two problems.
  const [problem, setProblem] = useState<BuilderProblem | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);

  if (state.status === 'loading') return <LoadingState title="Loading datasets…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  const dataset = state.value.find((entry) => entry.key === datasetKey);
  const groupable = dataset?.columns.filter((column) => column.groupable) ?? [];

  return (
    <Card>
      <CardHeader
        title="New report"
        subtitle="Choose a dataset and a grouping. Reports never contain SQL."
      />
      <Form
        onSubmit={(event) => {
          event.preventDefault();
          // VALIDATION HERE IS A COURTESY TOO. The server validates the same
          // document and refuses the same things; this only spares somebody a
          // round trip for a mistake they can see.
          if (name.trim() === '')
            return setProblem({ field: 'name', message: 'Give the report a name.' });
          if (datasetKey === '')
            return setProblem({ field: 'dataset', message: 'Choose a dataset.' });
          if (groupBy === '')
            return setProblem({ field: 'groupBy', message: 'Choose something to group by.' });

          setProblem(undefined);
          setSubmitting(true);
          const key = `platform.${name
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '_')}`;
          gateway
            .createReport({
              key,
              name: name.trim(),
              visibility: 'tenant',
              document: {
                datasetKey,
                columns: [],
                filters: [],
                groupBy: [groupBy],
                aggregates: [{ measure: 'tasks', aggregation: 'count' }],
                sort: [{ key: groupBy, direction: 'asc' }],
                parameters: [],
                limit: 100,
              },
            })
            .then(
              () => {
                onCreated(key);
              },
              (error: unknown) => {
                setProblem({
                  message:
                    error instanceof ApiError
                      ? `${error.message}${error.detail === undefined ? '' : ` — ${error.detail}`}`
                      : 'Could not create the report',
                });
              },
            )
            .finally(() => {
              setSubmitting(false);
            });
          return undefined;
        }}
      >
        <FormSection title="Definition">
          <TextField
            label="Name"
            name="name"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
            {...fieldProblem(problem, 'name')}
          />
          <SelectField
            label="Dataset"
            name="dataset"
            value={datasetKey}
            placeholder="Choose a dataset"
            options={state.value.map((entry) => ({ value: entry.key, label: entry.label }))}
            onChange={(event) => {
              setDatasetKey(event.target.value);
              setGroupBy('');
            }}
            {...fieldProblem(problem, 'dataset')}
          />
          <SelectField
            label="Group by"
            name="groupBy"
            value={groupBy}
            placeholder={dataset === undefined ? 'Choose a dataset first' : 'Choose a column'}
            options={groupable.map((column) => ({ value: column.key, label: column.label }))}
            disabled={dataset === undefined}
            onChange={(event) => {
              setGroupBy(event.target.value);
            }}
            {...fieldProblem(problem, 'groupBy')}
          />
        </FormSection>
        {problem !== undefined && problem.field === undefined && (
          <Alert tone="danger" title={problem.message} />
        )}
        <FormActions submitLabel="Create report" onCancel={onCancel} submitting={submitting} />
      </Form>
    </Card>
  );
}

// ---- 5. Report viewer -------------------------------------------------------

export function ReportViewerScreen({
  gateway,
  reportKey,
  canExport,
}: {
  readonly gateway: AnalyticsGateway;
  readonly reportKey: string;
  readonly canExport: boolean;
}) {
  const { state, reload } = useAsync<ReportResult>(
    () => gateway.runReport(reportKey),
    [gateway, reportKey],
  );
  const [exported, setExported] = useState(false);
  const [exportError, setExportError] = useState<string | undefined>(undefined);

  if (state.status === 'loading') return <LoadingState title="Running the report…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  const grouped = state.value.columns.filter((column) => column.key.startsWith('count_'));
  const dimension = state.value.columns.find((column) => !column.key.startsWith('count_'));

  return (
    <div>
      <Card padding="flush">
        <CardHeader
          title={reportKey}
          subtitle={`${String(state.value.rowCount)} row${state.value.rowCount === 1 ? '' : 's'}, over records you may read.`}
          action={
            canExport ? (
              <Button
                variant="secondary"
                onClick={() => {
                  setExportError(undefined);
                  gateway.requestExport({ reportKey, format: 'csv' }).then(
                    () => {
                      setExported(true);
                    },
                    (error: unknown) => {
                      setExportError(
                        error instanceof ApiError ? error.message : 'Could not request the export',
                      );
                    },
                  );
                }}
              >
                Export as CSV
              </Button>
            ) : undefined
          }
        />
        {exported && (
          <Alert tone="info" title="Export requested">
            It appears in Export history when it is ready.
          </Alert>
        )}
        {exportError !== undefined && <Alert tone="danger" title={exportError} />}
        {state.value.truncated && (
          <Alert tone="warning" title="Showing the first page">
            This report reached its row limit. Narrow it with a filter.
          </Alert>
        )}
        {state.value.rows.length === 0 ? (
          <EmptyState
            icon="chart-bar"
            title="No rows"
            description="Nothing you can see matches this report."
          />
        ) : (
          <GridFrame>
            <GridScroll>
              <Table label={`Report ${reportKey}`}>
                <thead>
                  <Tr>
                    {state.value.columns.map((column) => (
                      <Th key={column.key}>{column.label}</Th>
                    ))}
                  </Tr>
                </thead>
                <tbody>
                  {state.value.rows.map((row, index) => (
                    <Tr key={String(row[dimension?.key ?? 'id'] ?? index)}>
                      {state.value.columns.map((column) => (
                        <Td key={column.key}>{String(row[column.key] ?? '—')}</Td>
                      ))}
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </GridScroll>
          </GridFrame>
        )}
      </Card>

      {dimension !== undefined && grouped.length > 0 && state.value.rows.length > 0 && (
        <Card>
          <CardHeader title="Grouped" subtitle="The same rows, drawn." />
          <BarChart
            label={`${reportKey} by ${dimension.label}`}
            primaryLabel={grouped[0]?.label ?? 'Count'}
            compareLabel="—"
            data={state.value.rows.map((row) => [
              String(row[dimension.key] ?? '—'),
              // NOT RECOMPUTED. The number is the one the API returned, over
              // the authorised relation; this only draws it.
              Number(row[grouped[0]?.key ?? ''] ?? 0),
              0,
            ])}
          />
        </Card>
      )}
    </div>
  );
}

// ---- 6. Export history ------------------------------------------------------

/** The words for each status. The SERVER decides which one a row is in. */
const EXPORT_STATUS: Readonly<Record<ExportEntry['status'], string>> = {
  queued: 'Queued',
  running: 'Rendering',
  ready: 'Ready',
  failed: 'Failed',
  stale: 'Access changed',
  expired: 'Expired',
};

export function ExportHistoryScreen({ gateway }: { readonly gateway: AnalyticsGateway }) {
  const { state, reload } = useAsync<readonly ExportEntry[]>(() => gateway.exports(), [gateway]);
  const [problem, setProblem] = useState<string | undefined>(undefined);

  if (state.status === 'loading') return <LoadingState title="Loading your exports…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  if (state.value.length === 0) {
    return (
      <Card>
        <EmptyState
          icon="download-simple"
          title="No exports yet"
          description="Export a report and it appears here."
        />
      </Card>
    );
  }

  return (
    <Card padding="flush">
      <CardHeader
        title="Export history"
        subtitle="Your own exports. Files expire, and a download is checked again when you ask for it."
      />
      {problem !== undefined && <Alert tone="danger" title={problem} />}
      <GridFrame>
        <GridScroll>
          <Table label="Export history">
            <thead>
              <Tr>
                <Th>Requested</Th>
                <Th>Format</Th>
                <Th>Rows</Th>
                <Th>Status</Th>
                <Th>Expires</Th>
                <Th>Download</Th>
              </Tr>
            </thead>
            <tbody>
              {state.value.map((entry) => (
                <Tr key={entry.id}>
                  <Td>{when(entry.requestedAt)}</Td>
                  <Td variant="secondary">{entry.format.toUpperCase()}</Td>
                  <Td variant="secondary">{entry.rowCount ?? '—'}</Td>
                  <Td>
                    <StatusChip
                      tone={
                        entry.status === 'ready'
                          ? 'success'
                          : entry.status === 'failed' || entry.status === 'stale'
                            ? 'danger'
                            : statusTone(entry.status)
                      }
                    >
                      {EXPORT_STATUS[entry.status]}
                    </StatusChip>
                  </Td>
                  <Td variant="secondary">{when(entry.expiresAt)}</Td>
                  <Td>
                    {entry.status === 'ready' ? (
                      <Button
                        variant="tertiary"
                        onClick={() => {
                          setProblem(undefined);
                          gateway.downloadExport(entry.id).then(
                            (ticket) => {
                              globalThis.location?.assign(ticket.url);
                            },
                            (error: unknown) => {
                              setProblem(
                                error instanceof ApiError
                                  ? `${error.message}${error.detail === undefined ? '' : ` — ${error.detail}`}`
                                  : 'Could not fetch the file',
                              );
                              reload();
                            },
                          );
                        }}
                      >
                        Download
                      </Button>
                    ) : (
                      <span>—</span>
                    )}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </GridScroll>
      </GridFrame>
    </Card>
  );
}

// ---- 7. Dashboard metric cards ---------------------------------------------

export function MetricCardsScreen({ gateway }: { readonly gateway: AnalyticsGateway }) {
  const { state, reload } = useAsync<readonly MetricCard[]>(() => gateway.metrics(), [gateway]);

  if (state.status === 'loading') return <LoadingState title="Loading your dashboard…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  if (state.value.length === 0) {
    return (
      <Card>
        <EmptyState icon="chart-bar" title="No metrics" description="Nothing to show yet." />
      </Card>
    );
  }

  return (
    <div>
      {state.value.map((metric) => (
        <Card key={metric.key}>
          <CardHeader title={metric.title} />
          {metric.unavailable !== undefined ? (
            /**
             * NOT ZERO, AND NOT AN EMPTY CARD.
             *
             * Zero is a data claim — "there are none of these" — and this
             * caller has been told nothing about whether the figure is large
             * or small. Omitting the card would leave them wondering whether
             * the dashboard is broken. Saying so plainly is the only honest
             * option of the three.
             */
            <EmptyState
              icon="lock-simple"
              title="Not available for your access"
              description={metric.unavailable.detail}
            />
          ) : (
            // A null with no refusal beside it should not occur; if it ever
            // does, a dash says "no value" and a 0 would say "none of these".
            <p aria-label={`${metric.title} value`}>{metric.total ?? '—'}</p>
          )}
        </Card>
      ))}
    </div>
  );
}
